import * as React from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/Button/Button";
import { Pencil, InfoIcon, CheckCircle, AlertTriangle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { BulkBatchStatusDetailsMetadata } from "@/api/fetchBulkBatchStatusDetailsMetadata/types";
import {
  CampaignEventData,
  CliFileStatus,
  BatchStatus,
} from "@/modules/administration/features/Stochastic/CampaignJobStatus/api/fetchCampaignJobStatusData/types";
import { useIsMobile } from "@/hooks/use-mobile";

interface CampaignJobStatusFiltersProps {
  batchStatuses: BatchStatus[];
  campaignWeekEventData?: CampaignEventData | null;
  cliFileStatus: CliFileStatus;
  bulkBatchStatusDetailsMetadata?: BulkBatchStatusDetailsMetadata | null;
  filters: {
    week?: number;
    year?: number;
    batchStatusId?: number;
  };
  isDataLoading: boolean;
  isDownloading: boolean;
  isSendingToFtp: boolean;
  isDownloadingSummary: boolean;
  isBilling: boolean;
  showBillButton: boolean;
  onDownloadCLIFileModalOpen?: () => void;
  onDownloadSummaryButtonClick?: () => void;
  onSendToFtpButtonClick?: () => void;
  onFilterChange: (key: string, value: number | undefined) => void;
  onUpdateBatchStatusButtonClick?: () => void;
  onBillBatchesButtonClick?: () => void;
}

const DownloadDisabledTooltip: React.FC<{
  message?: string | null;
  campaignWeekEventData?: CampaignEventData | null;
}> = ({ message, campaignWeekEventData }) => {
  let tooltipText = message || "CLI file cannot be downloaded at this time.";

  if (campaignWeekEventData) {
    tooltipText = `Week ${campaignWeekEventData.week}, ${campaignWeekEventData.year} was locked by ${campaignWeekEventData.name} on ${new Date(campaignWeekEventData.createdAt).toLocaleDateString()}`;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center justify-center">
            <InfoIcon className="h-4 w-4 text-gray-500 cursor-help" />
          </div>
        </TooltipTrigger>
        <TooltipContent className="bg-white text-gray-900 border border-gray-200">
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const HasPendingBatchesIndicator: React.FC<{
  hasPendingBatches: boolean;
}> = ({ hasPendingBatches }) => {
  const Icon = hasPendingBatches ? AlertTriangle : CheckCircle;
  const iconColor = hasPendingBatches ? "text-yellow-600" : "text-green-600";
  const batchesReadyTooltipText = hasPendingBatches
    ? "Pending: Some campaigns have iterations that haven't started yet for this week."
    : "Batches Ready: All batches for this week have been generated.";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center justify-center cursor-help">
            <Icon className={`h-5 w-5 ${iconColor}`} />
          </div>
        </TooltipTrigger>
        <TooltipContent className="bg-white text-gray-900 border border-gray-200 max-w-xs">
          <p>{batchesReadyTooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const CampaignJobStatusFilters: React.FC<CampaignJobStatusFiltersProps> = ({
  batchStatuses,
  campaignWeekEventData,
  cliFileStatus,
  bulkBatchStatusDetailsMetadata,
  filters,
  isDataLoading,
  isDownloading,
  isDownloadingSummary,
  isBilling,
  showBillButton,
  isSendingToFtp,
  onFilterChange,
  onDownloadCLIFileModalOpen,
  onDownloadSummaryButtonClick,
  onUpdateBatchStatusButtonClick,
  onBillBatchesButtonClick,
  onSendToFtpButtonClick,
}) => {
  const isMobile = useIsMobile();
  const currentDate = new Date();
  const systemYear = currentDate.getFullYear();
  const currentWeek = filters.week ?? 1;
  const currentYear = filters.year ?? systemYear;
  const yearOptions = [systemYear - 1, systemYear, systemYear + 1];
  const weekOptions = Array.from({ length: 52 }, (_, i) => i + 1);
  const isDownloadCLIFileButtonDisabled =
    isDataLoading || !cliFileStatus.isCLIFileCanBeDownloaded;
  const isEditStatusButtonDisabled =
    !bulkBatchStatusDetailsMetadata ||
    bulkBatchStatusDetailsMetadata.currentStatus === null ||
    bulkBatchStatusDetailsMetadata.currentStatus === "new";

  const editStatusTooltipText = isEditStatusButtonDisabled
    ? "Please download CLI file to enable status editing"
    : "";

  const shouldShowDownloadCliFileTooltip =
    isDownloadCLIFileButtonDisabled &&
    !isDataLoading &&
    !campaignWeekEventData &&
    cliFileStatus.message;

  const createSelectItem = (value: number) => (
    <SelectItem key={value} value={String(value)}>
      {value}
    </SelectItem>
  );

  const renderFilters = () => {
    // Mobile layout: Batch Status on its own row, Year and Week on second row
    if (isMobile) {
      return (
        <div className="space-y-4 w-full">
          {/* First row: Batch Status only */}
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Batch Status
            </label>
            <Select
              onValueChange={(value) =>
                onFilterChange(
                  "batchStatusId",
                  value === "all" ? undefined : Number(value),
                )
              }
              value={
                filters.batchStatusId ? String(filters.batchStatusId) : "all"
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Status</SelectLabel>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {batchStatuses && batchStatuses.length > 0
                    ? batchStatuses.map((status) => (
                        <SelectItem key={status.id} value={String(status.id)}>
                          {status.name}
                        </SelectItem>
                      ))
                    : null}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {/* Second row: Year and Week side by side */}
          <div className="flex gap-2 w-full">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year
              </label>
              <Select
                onValueChange={(value) => onFilterChange("year", Number(value))}
                value={String(currentYear)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Years</SelectLabel>
                    {yearOptions.map(createSelectItem)}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Week
              </label>
              <div className="flex gap-2 items-center">
                <Select
                  onValueChange={(value) =>
                    onFilterChange("week", Number(value))
                  }
                  value={String(currentWeek)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a week" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Weeks</SelectLabel>
                      {weekOptions.map(createSelectItem)}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {!isDataLoading && (
                  <HasPendingBatchesIndicator
                    hasPendingBatches={cliFileStatus.hasPendingBatches}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Desktop/tablet layout: all dropdowns in a single row
    return (
      <div className="flex gap-4 items-end">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Batch Status
          </label>
          <Select
            onValueChange={(value) =>
              onFilterChange(
                "batchStatusId",
                value === "all" ? undefined : Number(value),
              )
            }
            value={
              filters.batchStatusId ? String(filters.batchStatusId) : "all"
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Status</SelectLabel>
                <SelectItem value="all">All Statuses</SelectItem>
                {batchStatuses && batchStatuses.length > 0
                  ? batchStatuses.map((status) => (
                      <SelectItem key={status.id} value={String(status.id)}>
                        {status.name}
                      </SelectItem>
                    ))
                  : null}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 min-w-[120px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Year
          </label>
          <Select
            onValueChange={(value) => onFilterChange("year", Number(value))}
            value={String(currentYear)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a year" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Years</SelectLabel>
                {yearOptions.map(createSelectItem)}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 min-w-[120px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Week
          </label>
          <Select
            onValueChange={(value) => onFilterChange("week", Number(value))}
            value={String(currentWeek)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a week" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Years</SelectLabel>
                {weekOptions.map(createSelectItem)}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {!isDataLoading && (
          <div className="pb-1.5 flex-shrink-0">
            <HasPendingBatchesIndicator
              hasPendingBatches={cliFileStatus.hasPendingBatches}
            />
          </div>
        )}
      </div>
    );
  };

  const renderLockMessage = () => {
    if (!campaignWeekEventData) return null;

    const lockDate = new Date(
      campaignWeekEventData.createdAt,
    ).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    return (
      <div className="bg-green-100 border border-green-300 rounded-lg px-3 py-2">
        <div className="flex items-center gap-2 text-sm text-green-800 font-bold">
          <CheckCircle className="h-4 w-4 stroke-2" />
          <span>
            Week {campaignWeekEventData.week}, {campaignWeekEventData.year} was
            locked by {campaignWeekEventData.employee.fullName} on {lockDate}
          </span>
        </div>
      </div>
    );
  };

  const renderMobileDownloadButtons = () => (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex gap-2">
        <div className="flex-1">
          <Button
            className="w-full"
            disabled={isDownloadingSummary}
            onClick={onDownloadSummaryButtonClick}
            size="sm"
          >
            {isDownloadingSummary ? "Downloading..." : "Download Summary"}
          </Button>
        </div>
      </div>
      <div className="flex gap-2">
        <div
          className={
            shouldShowDownloadCliFileTooltip
              ? "flex-1 flex gap-2 items-center"
              : "flex-1"
          }
        >
          <Button
            className="w-full"
            disabled={isDownloadCLIFileButtonDisabled}
            onClick={onDownloadCLIFileModalOpen}
            size="sm"
          >
            {isDownloading ? "Downloading..." : "Download CLI file"}
          </Button>
          {shouldShowDownloadCliFileTooltip && (
            <DownloadDisabledTooltip
              campaignWeekEventData={campaignWeekEventData}
              message={cliFileStatus.message}
            />
          )}
        </div>
        <div className="flex-1">
          <Button
            className="w-full"
            disabled={!campaignWeekEventData || isSendingToFtp}
            onClick={onSendToFtpButtonClick}
            size="sm"
          >
            {isSendingToFtp ? "Sending..." : "Send FTP"}
          </Button>
        </div>
      </div>
    </div>
  );

  const renderMobileLayout = () => (
    <div className="mb-4 space-y-4">
      {renderLockMessage()}
      {renderFilters()}

      <div className="space-y-3">
        {showBillButton && (
          <Button
            className="w-full"
            onClick={onBillBatchesButtonClick}
            size="sm"
          >
            {isBilling ? "Billing..." : "Bill Selected Batches"}
          </Button>
        )}

        <div className="space-y-2">
          {renderMobileDownloadButtons()}

          {bulkBatchStatusDetailsMetadata && (
            <div className="flex gap-2 items-center">
              <Button
                aria-label="Edit Status"
                className="w-full"
                disabled={isEditStatusButtonDisabled}
                onClick={onUpdateBatchStatusButtonClick}
                size="sm"
                variant="default"
              >
                <span>Edit Status</span>
              </Button>
              {isEditStatusButtonDisabled && editStatusTooltipText && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center justify-center">
                        <InfoIcon className="h-4 w-4 text-gray-500 cursor-help" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-white text-gray-900 border border-gray-200">
                      <p>{editStatusTooltipText}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderDesktopLayout = () => (
    <div className="mb-4 space-y-4">
      {renderLockMessage()}

      <div className="flex flex-wrap gap-4 items-end justify-between">
        {/* Filters section */}
        <div className="flex-1 max-w-fit">{renderFilters()}</div>

        {/* Buttons section */}
        <div className="flex gap-2 flex-wrap justify-end flex-shrink-0">
          {showBillButton && (
            <Button
              className="flex-shrink-0"
              onClick={onBillBatchesButtonClick}
              size="sm"
            >
              {isBilling ? "Billing..." : "Bill Selected Batches"}
            </Button>
          )}

          <Button
            className="flex-shrink-0"
            disabled={isDownloadingSummary}
            onClick={onDownloadSummaryButtonClick}
            size="sm"
          >
            {isDownloadingSummary ? "Downloading..." : "Download Summary"}
          </Button>

          <div className="flex items-center gap-2 flex-shrink-0">
            {shouldShowDownloadCliFileTooltip && (
              <DownloadDisabledTooltip
                campaignWeekEventData={campaignWeekEventData}
                message={cliFileStatus.message}
              />
            )}
            <Button
              className="flex-shrink-0"
              disabled={isDownloadCLIFileButtonDisabled}
              onClick={onDownloadCLIFileModalOpen}
              size="sm"
            >
              {isDownloading ? "Downloading..." : "Download CLI file"}
            </Button>
          </div>

          <Button
            className="flex-shrink-0"
            disabled={!campaignWeekEventData || isSendingToFtp}
            onClick={onSendToFtpButtonClick}
            size="sm"
          >
            {isSendingToFtp ? "Sending..." : "Send FTP"}
          </Button>

          {bulkBatchStatusDetailsMetadata && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                aria-label="Edit"
                className="flex-shrink-0"
                disabled={isEditStatusButtonDisabled}
                onClick={onUpdateBatchStatusButtonClick}
                size="sm"
                variant="default"
              >
                <Pencil className="w-4 h-4" />
              </Button>
              {isEditStatusButtonDisabled && editStatusTooltipText && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center justify-center">
                        <InfoIcon className="h-4 w-4 text-gray-500 cursor-help" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-white text-gray-900 border border-gray-200">
                      <p>{editStatusTooltipText}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return isMobile ? renderMobileLayout() : renderDesktopLayout();
};

export default CampaignJobStatusFilters;
